export type IconName =
  | 'arrowRight'
  | 'close'
  | 'delete'
  | 'menu'
  | 'check'
  | 'dot'
  | 'minus'
  | 'warning'
  | 'info'
  | 'copy'
  | 'timer'
  | 'calendar'
  | 'home'
  | 'games'
  | 'activeGames'
  | 'friends'
  | 'notifications'
  | 'profile'
  | 'settings'
  | 'logout'
  | 'admin'
  | 'lock';

/**
 * Navigation glyphs backed by the shared pixel-art icon set
 * (`/assets/shared/icons/nav/…`, docs/ART_ASSETS.md §2) rather than by a line
 * path. `nav-items.ts` and `Header.tsx` still reference every glyph by
 * `IconName` only, so this map is the single place the artwork is bound
 * (docs/UI_GUIDELINES.md §5 "one icon mapping").
 *
 * These files are derived from the master art in
 * `assets-src/icons/` by `scripts/build-nav-icons.mjs` — they are
 * trimmed to a shared content box, keyed to transparency, and reduced to 128px
 * so the header stays inside the asset budget in UI_GUIDELINES.md §9. Re-run
 * that script after replacing a master; do not hand-edit the derived files.
 *
 * Names without final artwork (`admin`) keep their line path below, as do all
 * non-navigation glyphs.
 */
export const NAV_ART_DIR = '/assets/shared/icons/nav';
export const NAV_ART: Partial<Record<IconName, string>> = {
  home: 'home',
  games: 'collection',
  activeGames: 'active-games',
  friends: 'friends',
  notifications: 'notifications',
  profile: 'profile',
  settings: 'settings',
  logout: 'logout',
  menu: 'menu',
  // The shared pixel-art dismiss glyph, used by destructive per-item
  // controls (the notification delete button). `close` deliberately keeps
  // its line path below: it is the dialog dismiss affordance, which sits on
  // parchment at small sizes where the line glyph reads better, and is
  // reused at 14px inside the lobby invitee chips.
  delete: 'close',
};

/** Intrinsic size of every file in `NAV_ART`, declared per UI_GUIDELINES.md §9. */
export const NAV_ART_SIZE = 128;

export const PATHS: Record<IconName, string> = {
  arrowRight: 'M5 12h14m-6-6 6 6-6 6',
  close: 'M6 6l12 12M18 6 6 18',
  menu: 'M4 7h16M4 12h16M4 17h16',
  /** Correct/positive confirmation — used generically (game tile states, form success, etc.), never game-specific. */
  check: 'M4 12l5 5L20 6',
  /** A small filled indicator ("present but not exact") — a plain circle, not a game-specific glyph. */
  dot: 'M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0',
  /** A plain "not applicable / absent" glyph. */
  minus: 'M5 12h14',
  /** Caution glyph — pairs with (never replaces) explicit warning/danger wording. */
  warning: 'M12 3 2 20h20L12 3Zm0 6v5m0 3h.01',
  /** Neutral-information glyph — pairs with explicit wording. */
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-9v5m0-8h.01',
  /** Fallback for the `delete` art above — the same cross the dialog close button draws. */
  delete: 'M6 6l12 12M18 6 6 18',
  copy: 'M8 8V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3M8 8H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-3M8 8h6a2 2 0 0 1 2 2v6',
  timer: 'M12 8v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
  /** Date glyph — pairs with a written date, never replaces it. */
  calendar:
    'M8 3v3M16 3v3M3.5 9.5h17M5.5 5h13a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z',
  // --------------------------------------------------------------------------
  // Primary-navigation glyphs (docs/UI_GUIDELINES.md §5, §8). Every name here
  // except `admin` now renders the final artwork registered in
  // `NAV_ART` above; these paths are the fallback that stays in the bundle for
  // any name whose art has not landed. Keep an entry for each so a name is
  // never left without a glyph.
  // --------------------------------------------------------------------------
  home: 'M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10',
  games: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  activeGames: 'M8 5v14l11-7z',
  friends:
    'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 5.2a3 3 0 0 1 0 5.6M17 14.3c2.3.6 4 2.5 4 5.7',
  notifications: 'M6 16V11a6 6 0 1 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0',
  profile: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 20c0-4 3-6 7-6s7 2 7 6',
  settings:
    'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1',
  logout: 'M15 12H4m0 0 4-4m-4 4 4 4M10 4h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-8',
  admin: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4',
  /** A locked/unavailable cosmetic option — always paired with visually-hidden "(locked)" text, never the sole indicator. */
  lock: 'M7 11V7a5 5 0 0 1 10 0v4M5 11h14v9H5z',
};

export interface IconProps {
  name: IconName;
  /** Sizing (and, for line icons, colour) utilities. Defaults to the 20px control size. */
  className?: string;
}

/**
 * The one icon component. Most names render as a line path that inherits
 * `currentColor`; the navigation names in `NAV_ART` render their shared
 * artwork instead. Both are decorative — every caller is an element that
 * already carries its own accessible name (a nav link's `aria-label` plus its
 * visually-hidden label, a button's label, a tile's state phrase), so the icon
 * itself is always `aria-hidden` and never the sole carrier of meaning
 * (docs/UI_GUIDELINES.md §9).
 */
export function Icon({ name, className = 'h-5 w-5' }: IconProps) {
  const art = NAV_ART[name];
  if (art) {
    return (
      <img
        src={`${NAV_ART_DIR}/${art}.png`}
        alt=""
        width={NAV_ART_SIZE}
        height={NAV_ART_SIZE}
        className={className}
        aria-hidden="true"
        draggable={false}
      />
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
